import { useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { ServiceGroup, URL_CATEGORIES } from '@/types/serviceGroup';
import { UserSession } from '@/types/userSession';
import { SERVICE_GROUP_ID } from '@/constants';

export const useExtractLinksFromService = (service: ServiceGroup) => {
  const userSession = useAppSelector(authStore.selectors.userSessionSelector);
  const isManager = useAppSelector(authStore.selectors.isManagerSelector);
  return extractLinksFromService(service, userSession, isManager);
};

const AUTH_URL_REGEX = /https?:\/\/(stg\d?\.)?auth\.globis\.co\.jp/;
const EMBA_URL_REGEX =
  /https:\/\/(stg-)?learning\.digitalglobis\.jp\/eLM\/admin\/dglp_check\.cfm/;
// SAML認証URLの正規表現
// マッチする条件:
//   - プロトコル: httpsのみ
//   - ドメイン: auth.globis.co.jp または stg.auth.globis.co.jp または stg{数字}.auth.globis.co.jp
//   - パス: /auth/saml/idp で始まる
const SAML_AUTH_URL_REGEX =
  /https:\/\/(stg\d?\.)?auth\.globis\.co\.jp\/auth\/saml\/idp/;

// FIXME: API側に処理持っててほしい
const extractLinksFromService = (
  service: ServiceGroup,
  session: UserSession,
  isManager: boolean,
) => {
  const attachedLinks = service.urls
    .filter((link) => {
      if (isManager) {
        return true;
      }
      return [
        URL_CATEGORIES.LANDINGPAGE,
        URL_CATEGORIES.ADMIN,
        URL_CATEGORIES.OTHERSSO,
      ].includes(link.category);
    })
    .map((link) => {
      const { url, category } = link;
      const { gloplaId, token } = session;
      const delimiter = url.indexOf('?') === -1 ? '?' : '&';
      const isTargetService = [
        SERVICE_GROUP_ID.hodai,
        SERVICE_GROUP_ID.gu,
        SERVICE_GROUP_ID.hodaiplus,
        SERVICE_GROUP_ID.newhodaiplus,
        SERVICE_GROUP_ID.newemba,
      ].includes(service.id);
      const isAdminCategory = category === URL_CATEGORIES.ADMIN;
      // OIDC移行した研修プロセス管理システム(PMS)
      const isGlsOtherSsoCategory =
        service.id === SERVICE_GROUP_ID.gls &&
        category === URL_CATEGORIES.OTHERSSO &&
        !SAML_AUTH_URL_REGEX.test(url);
      const isEmbaUrl = EMBA_URL_REGEX.test(url);
      const isGloplaIdIncluded = link.url.includes('glopla_id');

      if (
        !url.startsWith('http') &&
        category === URL_CATEGORIES.SUBSCRIPTIONLIST
      ) {
        link.url = `${url}${delimiter}service_group_id=${service.id}`;
      }

      /**
       * OIDC移行済みサービスのSSO組織選択スキップ機能
       * 概要：
       * OIDC移行済みのサービスでSSOログイン時に組織選択画面をスキップするため、
       * 特定の条件下でURLにglopla_idパラメータを付与する。
       *
       * 条件1：研修プロセス管理システム(PMS)
       * 以下の全ての条件を満たす場合にglopla_idを付与：
       * - ServiceGroupが「gls」
       * - categoryが「other_sso」（PMS）
       * - SAML認証URL（{auth URL}/auth/saml/idp）以外のURL
       * - URLにglopla_idパラメータが含まれていない
       *
       * 条件2：対象サービスグループの受講管理
       * 以下の全ての条件を満たす場合にglopla_idを付与：
       * - ServiceGroupが以下のいずれか：
       *   ・GLOBIS 学び放題
       *   ・Unlimited
       *   ・GLOBIS 学び放題プラス（旧）
       *   ・GLOBIS 学び放題プラス（新）
       *   ・eMBA単体法人
       * - categoryが「admin」（受講管理）
       * - eMBAの特定URL（https://learning.digitalglobis.jp/eLM/admin/dglp_check.cfm）以外
       * - URLにglopla_idパラメータが含まれていない
       */
      if (
        (isGlsOtherSsoCategory && !isGloplaIdIncluded) ||
        (isTargetService &&
          isAdminCategory &&
          !isEmbaUrl &&
          !isGloplaIdIncluded)
      ) {
        link.url = `${url}${delimiter}glopla_id=${gloplaId}`;
      }

      if (AUTH_URL_REGEX.test(url)) {
        link.url = `${url}${delimiter}at=${gloplaId}:${token}`;
      }
      return link;
    });

  return {
    admin: attachedLinks.filter(
      (link) => link.category === URL_CATEGORIES.ADMIN,
    ),
    subscribe:
      attachedLinks.find(
        (link) => link.category === URL_CATEGORIES.SUBSCRIBE,
      ) || null,
    subscriptionList:
      attachedLinks.find(
        (link) => link.category === URL_CATEGORIES.SUBSCRIPTIONLIST,
      ) || null,
    landingPage:
      attachedLinks.find(
        (link) => link.category === URL_CATEGORIES.LANDINGPAGE,
      ) || null,
    otherSso:
      attachedLinks.find((link) => link.category === URL_CATEGORIES.OTHERSSO) ||
      null,
  };
};
